// Free-form text (a chat note) can contain links the user typed. There is no link entity on a
// note and no unfurl service, so both the Links list in the notes media view and the clickable
// links inside a chat bubble are derived from the note's own text — by this module, so the two
// surfaces always agree on what counts as a link and where it starts and ends.

// An explicit http(s) URL, or a bare `www.` host. Trailing sentence punctuation is trimmed off
// afterwards so "see www.g2.com." doesn't pull the full stop into the href. Whitespace, angle
// brackets and straight quotes end a match — and so do the curly quotes (U+2018 to U+201F) a smart
// keyboard or pasted rich text substitutes for them, or "https://example.com’s policy" would put the
// `’s` in the href and "“https://example.com”" would keep the closing quote, while the
// straight-quote spellings of both already end cleanly. A raw curly quote is never a legal URL
// character; one that is genuinely part of a link arrives percent-encoded.
const URL_PATTERN = /(https?:\/\/[^\s<>"'\u2018-\u201F]+|www\.[^\s<>"'\u2018-\u201F]+)/gi;

// Two links typed back to back with only a separator between them ("https://a.com,https://b.com")
// come back as a single match, because URL_PATTERN runs to the next whitespace. A `,` or `;` that is
// immediately followed by another URL start is therefore the end of THIS link, not part of it.
const SEPARATOR_BEFORE_NEXT_URL = /[,;](?=https?:\/\/|www\.)/i;

// Cut a match short at the separator that starts the next link, leaving that link for the next pass.
const truncateAtNextLink = (candidate: string): string => {
	const separatorIndex = candidate.search(SEPARATOR_BEFORE_NEXT_URL);
	return separatorIndex === -1 ? candidate : candidate.slice(0, separatorIndex);
};

// Characters that end the sentence rather than the URL. `<`, `>`, `"`, `'` and the curly quotes are
// absent on purpose: URL_PATTERN already excludes those, so they can never end a match.
const TRAILING_SENTENCE_PUNCTUATION = '.,;:!?';

// Markdown marks get into a match because URL_PATTERN does not exclude them, so "`https://x.com`"
// pulls the closing backtick in — but every one of them is also a legal URL character, and a path or
// a base64url token can genuinely end in `_` or `~`. So they are only trimmed when the SAME mark
// sits immediately before the match, which is the wrapper's opening half: the same "the opener is
// outside the match" tell the bracket rule below uses.
const MARKDOWN_WRAPPER_MARKS = '`*~_';

// A closing bracket is only sentence punctuation when the URL never opened one — compare
// "(see https://example.com/a)", where the `)` closes the aside, with
// "https://en.wikipedia.org/wiki/Foo_(bar)", where it belongs to the path. The opening bracket of an
// aside sits outside the match (URL_PATTERN starts at `https`/`www.`), so an unmatched closer is the
// tell.
const OPENING_BRACKET_BY_CLOSING: { [closingBracket: string]: string } = {
	')': '(',
	']': '[',
	'}': '{',
};

const countCharacter = (text: string, character: string): number =>
	text.split(character).length - 1;

// Peel sentence punctuation off the end of a matched URL, leaving brackets that the URL balances and
// Markdown marks that nothing opened. `precedingCharacter` is the character immediately before the
// match in the source text, or '' when the match starts the text.
const trimTrailingPunctuation = (candidate: string, precedingCharacter: string): string => {
	let trimmed = candidate;

	while (trimmed.length > 0) {
		const lastCharacter = trimmed.charAt(trimmed.length - 1);
		const openingBracket = OPENING_BRACKET_BY_CLOSING[lastCharacter];

		if (openingBracket) {
			if (countCharacter(trimmed, lastCharacter) <= countCharacter(trimmed, openingBracket)) {
				break;
			}
		} else if (MARKDOWN_WRAPPER_MARKS.indexOf(lastCharacter) !== -1) {
			if (lastCharacter !== precedingCharacter) {
				break;
			}
		} else if (TRAILING_SENTENCE_PUNCTUATION.indexOf(lastCharacter) === -1) {
			break;
		}

		trimmed = trimmed.slice(0, -1);
	}

	return trimmed;
};

// A candidate that already declared a scheme is a link on that basis alone — an explicit
// "http://192.168.1.20/status" or "http://localhost:3000" is a place someone meant to send you, and
// has no TLD to show for it — it does still have to name a host, so a bare "http://" left behind by
// trimming an ellipsis is not one. A bare "www." candidate has only its shape to go on, so it still
// has to carry a TLD-looking suffix, which keeps things like "3.5" or a lone "www." out of the list.
const isLinkLike = (candidate: string): boolean =>
	/^https?:\/\/[^\s/?#]/i.test(candidate) ||
	(/^www\./i.test(candidate) && /\.[a-z]{2,}/i.test(candidate));

export interface LinkTextSegment {
	/** The text exactly as it should read on screen. */
	value: string;
	/** Present only on link segments — the href to open. */
	href?: string;
}

/** A scheme-less link ("www.g2.com") still needs one before it can be used as an href. */
export const normalizeLinkHref = (url: string): string =>
	/^https?:\/\//i.test(url) ? url : `https://${url}`;

/** Host of a link, `www.` included, the way the design labels a link row. */
export const getLinkHostname = (url: string): string => {
	try {
		return new URL(normalizeLinkHref(url)).hostname;
	} catch (error) {
		return url;
	}
};

/**
 * Best-effort name for a link that has no surrounding text to borrow from: the last path segment,
 * de-slugified ("/product-launch-2026" → "product launch 2026"), else the host.
 */
export const getLinkFallbackTitle = (url: string): string => {
	try {
		const parsed = new URL(normalizeLinkHref(url));
		const pathSegments = parsed.pathname.split('/').filter(Boolean);
		const lastSegment = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';
		if (lastSegment) {
			const readable = decodeURIComponent(lastSegment)
				.replace(/\.[a-z0-9]{1,5}$/i, '')
				.replace(/[-_+]+/g, ' ')
				.trim();
			if (readable) {
				return readable;
			}
		}
		return parsed.hostname;
	} catch (error) {
		return url;
	}
};

/**
 * Split text into alternating plain and link segments, in order. Text with no links comes back as
 * a single plain segment, and empty text as an empty list.
 */
export const splitTextByLinks = (text: string): LinkTextSegment[] => {
	if (!text) {
		return [];
	}

	const segments: LinkTextSegment[] = [];
	// Tracks where the next plain segment starts. Whatever a match gave back — trailing punctuation,
	// or the next link — belongs to the text after the link that was emitted.
	let plainTextStart = 0;

	URL_PATTERN.lastIndex = 0;
	let match = URL_PATTERN.exec(text);
	while (match !== null) {
		const candidate = trimTrailingPunctuation(
			truncateAtNextLink(match[0]),
			text.charAt(match.index - 1)
		);
		if (isLinkLike(candidate)) {
			if (match.index > plainTextStart) {
				segments.push({ value: text.slice(plainTextStart, match.index) });
			}
			segments.push({ value: candidate, href: normalizeLinkHref(candidate) });
			plainTextStart = match.index + candidate.length;
		}
		// Resume the scan immediately after whatever this match kept, not after the whole match: what
		// it gave back can itself hold the next link. This applies even when the kept part was not a
		// link ("www.,https://a.com" keeps a bare "www.", and the real link is in the tail). Guarded
		// on a non-empty candidate so the cursor always moves forward.
		if (candidate.length > 0) {
			URL_PATTERN.lastIndex = match.index + candidate.length;
		}
		match = URL_PATTERN.exec(text);
	}

	if (plainTextStart < text.length) {
		segments.push({ value: text.slice(plainTextStart) });
	}

	return segments;
};
